import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import {
	Button,
	Count,
	GraphQLErrorMessage,
	Paragraph,
	Pill,
	Popover,
	QuillText,
	UserRow,
	tokens,
} from '@treams/treams-ui';

import { readinessLabelKey, readinessTint } from './successorReadiness';

const Trigger = styled.button`
	background: none;
	border: 0;
	padding: 0;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
`;

const Nomination = styled(Box)`
	&:not(:last-child) {
		border-bottom: 1px solid ${tokens.border.subtle};
	}
`;

function NominationItem({
	nomination,
	onWithdrawn,
}: {
	nomination: graphql.JobPositionSuccessorFragment;
	onWithdrawn: () => void;
}) {
	const { t } = useTranslation();
	const [archive, { loading, error }] = graphql.useArchiveJobPositionSuccessorMutation({
		refetchQueries: ['JobPositionSuccessors', 'JobTitleSuccessors'],
	});

	const handleWithdraw = async () => {
		try {
			await archive({ variables: { input: { id: nomination.id } } });
			onWithdrawn();
		} catch {
			// error state is shown via GraphQLErrorMessage
		}
	};

	return (
		<Nomination py={3}>
			<Flex justifyContent="space-between" alignItems="center">
				<UserRow
					user={nomination.createdBy}
					subtitle={moment(nomination.createdAt).format('LL')}
					avatarProps={{ size: 32 }}
				/>
				<Pill tint={readinessTint(nomination.readiness)} small>
					{t(readinessLabelKey(nomination.readiness))}
				</Pill>
			</Flex>
			<Box mt={2}>
				<QuillText value={nomination.motivation} />
			</Box>
			{error?.graphQLErrors[0] && (
				<Box mt={2}>
					<GraphQLErrorMessage error={error.graphQLErrors[0]} />
				</Box>
			)}
			{nomination.viewerCanArchive && (
				<Flex mt={2} justifyContent="flex-end">
					<Button
						small
						type="destructive"
						disableSuccess
						loading={loading}
						title={t('analytics.talentReview.succession.withdraw')}
						onClick={handleWithdraw}
					/>
				</Flex>
			)}
		</Nomination>
	);
}

// "Keer genoemd" cell: a count that opens a popover listing every nomination
// for this candidate (nominator, date, readiness, motivation) with a withdraw
// button on the viewer's own nominations.
export function SuccessorNominationsPopover({
	nominations,
	onWithdrawn,
}: {
	nominations: graphql.JobPositionSuccessorFragment[];
	onWithdrawn: () => void;
}) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);

	return (
		<Popover.Target
			visible={open}
			onDismiss={() => setOpen(false)}
			horizontalAlign="right"
			style={{ flexShrink: 0 }}
			contents={
				<Box px={3} py={1} width={400} style={{ maxHeight: 420, overflowY: 'auto' }}>
					<Paragraph color="slateBlue" mt={2}>
						{t('analytics.talentReview.succession.nominatedBy')}
					</Paragraph>
					{nominations.map((nomination) => (
						<NominationItem
							key={nomination.id}
							nomination={nomination}
							onWithdrawn={() => {
								onWithdrawn();
								if (nominations.length <= 1) {
									setOpen(false);
								}
							}}
						/>
					))}
				</Box>
			}
		>
			<Trigger
				type="button"
				aria-label={t('analytics.talentReview.succession.column.mentions')}
				onClick={() => setOpen((current) => !current)}
			>
				<Count count={nominations.length} variant="count" />
			</Trigger>
		</Popover.Target>
	);
}
